import React, { useState } from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import { LifeThemeId } from '../../types/advancedLifeAnalysis';
import { Sparkles, Calendar, Clock, Compass, ShieldCheck, CheckCircle2, AlertTriangle, ArrowRight, Share2, Copy, Check } from 'lucide-react';
import { NAKSHATRAS } from '../../utils/vedicEngine';
import { calculateTaraBala } from '../../utils/muhurtaEngine';

interface LifeDomainMuhurtaAidProps {
  kundali: CompleteKundali;
  themeId: LifeThemeId;
  lang: Language;
  onOpenDetailedMuhurta?: (milestoneId?: string) => void;
  className?: string;
}

const DOMAIN_MUHURTA_PRESETS: Record<LifeThemeId, {
  labelEn: string;
  labelHi: string;
  milestoneId: string;
  idealTithis: string;
  idealVara: string;
  idealDirection: string;
  idealDirectionHi: string;
  purposeRationale: string;
  purposeRationaleHi: string;
}> = {
  career_job: {
    labelEn: 'Job Joining, Contract Signing & Enterprise Inauguration',
    labelHi: 'कार्यभार ग्रहण, अनुबंध हस्ताक्षर एवं व्यापार उद्घाटन',
    milestoneId: 'business_incorporation',
    idealTithis: 'Dwitiya, Tritiya, Panchami, Dashami, Ekadashi, Trayodashi (Shukla Paksha)',
    idealVara: 'Wednesday (Mercury), Thursday (Jupiter), Friday (Venus)',
    idealDirection: 'East (Purva) — for intellectual clarity and professional ascent',
    idealDirectionHi: 'पूर्व दिशा — बौद्धिक स्पष्टता एवं कार्यक्षेत्र में पदोन्नति हेतु',
    purposeRationale: 'Initiating professional ventures during benefic hora and Shubha Tithi fosters enduring career elevation, commercial stability, and executive recognition.',
    purposeRationaleHi: 'शुभ होरा एवं शुभ तिथि में कार्यभार ग्रहण करने से जातक का अधिकार, सम्मान एवं व्यावसायिक स्थायित्व निर्बाध रूप से प्राप्त होता है।'
  },
  wealth_finance: {
    labelEn: 'Banking, Major Investments, Asset Purchase & Locker Opening',
    labelHi: 'बैंकिंग, महत्वपूर्ण निवेश, स्वर्ण/सम्पत्ति क्रय एवं लॉकर आरम्भ',
    milestoneId: 'open_bank_account',
    idealTithis: 'Tritiya, Panchami, Saptami, Dashami, Trayodashi, Purnima',
    idealVara: 'Thursday (Guru), Friday (Shukra), Wednesday (Budha)',
    idealDirection: 'North (Uttara) — ruled by Kubera & Mercury for wealth accumulation',
    idealDirectionHi: 'उत्तर दिशा — धन कुबेर एवं बुध के आधिपत्य में संचय एवं समृद्धि हेतु',
    purposeRationale: 'Executing financial deposits and asset contracts during Dhanadhipati transits stimulates capital compounding, liquidity, and protection against depreciation.',
    purposeRationaleHi: 'धनाधिपति के शुभ गोचर में निवेश अनुबंध करने से धन संचय में निरंतरता एवं वित्तीय सुरक्षा सुनिश्चित होती है।'
  },
  house_property: {
    labelEn: 'Bhoomi Pujan, Griha Pravesh & Property Registry',
    labelHi: 'भूमि पूजन, गृह प्रवेश एवं संपत्ति रजिस्ट्री/अनुबंध',
    milestoneId: 'griha_pravesh',
    idealTithis: 'Tritiya, Panchami, Saptami, Dashami, Trayodashi (Uttarayana preferred)',
    idealVara: 'Monday, Wednesday, Thursday, Friday (Avoid Tuesday/Sunday)',
    idealDirection: 'North-East (Ishanya) — for divine sanctity and structural harmony',
    idealDirectionHi: 'ईशान कोण (उत्तर-पूर्व) — वास्तु शुद्धि एवं गृह सौख्य हेतु',
    purposeRationale: 'Commencing residential occupancy in Sthira (Fixed) nakshatras establishes enduring peace, familial concord, and grounded Vastu harmony.',
    purposeRationaleHi: 'स्थिर नक्षत्रों में गृह प्रवेश से वास्तु दोषों का शमन एवं परिवार में दीर्घकालिक सुख-शांति की प्राप्ति होती है।'
  },
  vehicle: {
    labelEn: 'Vehicle Delivery, Machinery Installation & Drive Commencement',
    labelHi: 'वाहन क्रय/डिलीवरी, यंत्र प्रतिष्ठा एवं प्रथम चालन',
    milestoneId: 'vehicle_purchase',
    idealTithis: 'Dwitiya, Tritiya, Panchami, Dashami, Ekadashi, Purnima',
    idealVara: 'Wednesday, Thursday, Friday (Avoid Rahu Kalam & Shani Hora)',
    idealDirection: 'East or North — for safe transit and mechanical longevity',
    idealDirectionHi: 'पूर्व अथवा उत्तर दिशा — सुरक्षित यात्रा एवं यांत्रिक सुरक्षा हेतु',
    purposeRationale: 'Taking vehicular delivery during Chara (Movable) or Mridu nakshatras channels Venusian grace for road safety, mechanical endurance, and smooth transit.',
    purposeRationaleHi: 'चर अथवा मृदु नक्षत्र में वाहन ग्रहण करने से शुक्र की कृपा एवं सुरक्षित यात्रा का शुभ फल प्राप्त होता है।'
  },
  marriage_relationship: {
    labelEn: 'Vagdanam (Engagement), Vivaha Sanskara & Matrimonial Alliance',
    labelHi: 'वाग्दान (सगाई), विवाह संस्कार एवं परिणय सूत्र',
    milestoneId: 'vivaha',
    idealTithis: 'Dwitiya, Tritiya, Panchami, Saptami, Dashami, Ekadashi, Trayodashi',
    idealVara: 'Thursday, Friday, Monday, Wednesday',
    idealDirection: 'East or North-East — for spiritual bonding and mutual prosperity',
    idealDirectionHi: 'पूर्व अथवा ईशान कोण — दांपत्य सौहार्द एवं वंश वृद्धि हेतु',
    purposeRationale: 'Sanctifying nuptials free of Latta, Vedha, and Rikta Tithi deepens mutual devotion, enduring domestic stability, and auspicious familial harmony.',
    purposeRationaleHi: 'त्रिबल शुद्धि एवं लता-वेध रहित लग्न में परिणय से दांपत्य जीवन में आजीवन परस्पर अनुराग एवं समृद्धि बनी रहती है।'
  },
  education: {
    labelEn: 'Vidyarambha, Competitive Exam Application & Research Initiation',
    labelHi: 'विद्यारंभ, उच्च शिक्षा प्रवेश, प्रतियोगी परीक्षा आवेदन एवं शोध',
    milestoneId: 'vidyarambha',
    idealTithis: 'Dwitiya, Tritiya, Panchami (Saraswati Tithi), Dashami, Ekadashi',
    idealVara: 'Wednesday (Budha), Thursday (Brihaspati), Sunday',
    idealDirection: 'East (Purva) — facing the rising Sun for memory and intellect',
    idealDirectionHi: 'पूर्व दिशा — सूर्य एवं सरस्वती के आशीर्वाद से तीव्र स्मरण शक्ति हेतु',
    purposeRationale: 'Starting scholastic pursuits in Laghu or Mridu nakshatras awakens Buddhi and Medha shakti for disciplined retention, intellectual sharpness, and academic success.',
    purposeRationaleHi: 'लघु अथवा मृदु नक्षत्र में अध्ययन प्रारंभ करने से ज्ञानार्जन एवं एकाग्रता में अभूतपूर्व वृद्धि होती है।'
  },
  health_vitality: {
    labelEn: 'Surgery Scheduling, Treatment Start & Ayurvedic Rasayana',
    labelHi: 'शल्य चिकित्सा (सर्जरी), नवीन औषधि प्रारंभ एवं कायाकल्प',
    milestoneId: 'surgery',
    idealTithis: 'Panchami, Saptami, Dashami, Trayodashi (Avoid Rikta & Amavasya)',
    idealVara: 'Tuesday (for surgical extraction/healing under Mars), Thursday (for medicine)',
    idealDirection: 'East (Purva) — for vitality and cellular rejuvenation',
    idealDirectionHi: 'पूर्व दिशा — आरोग्य, तेज एवं शीघ्र स्वास्थ्य लाभ हेतु',
    purposeRationale: 'Commencing curative therapies under benefic Hora aligns Prana vayu to accelerate cellular vitality, physician precision, and post-operative recuperation.',
    purposeRationaleHi: 'शुभ होरा में चिकित्सा प्रारंभ करने से प्राण शक्ति का संवर्धन एवं शीघ्र स्वास्थ्य लाभ सिद्ध होता है।'
  },
  business: {
    labelEn: 'Trade Registration, Partnership Agreements & Capital Allocation',
    labelHi: 'व्यापार पंजीकरण, साझेदारी समझौता एवं नवीन पूँजी निवेश',
    milestoneId: 'business_incorporation',
    idealTithis: 'Dwitiya, Tritiya, Panchami, Dashami, Ekadashi, Trayodashi, Purnima',
    idealVara: 'Wednesday (Mercury), Thursday (Jupiter), Friday (Venus)',
    idealDirection: 'North (Uttara) — for financial liquidity and expansive trade networks',
    idealDirectionHi: 'उत्तर दिशा — व्यापार विस्तार एवं वित्तीय तरलता हेतु',
    purposeRationale: 'Launching commercial agreements under Shubha Tara Bala anchors stakeholder trust, commercial viability, and healthy compounding enterprise value.',
    purposeRationaleHi: 'शुभ तारा बल में व्यापारिक अनुबंध करने से साझेदारों में सामंजस्य एवं स्थायी लाभ का मार्ग प्रशस्त होता है।'
  },
  foreign_travel: {
    labelEn: 'Visa Application, Passport Submission & International Departure',
    labelHi: 'वीज़ा आवेदन, पासपोर्ट जमा एवं विदेश यात्रा प्रस्थान',
    milestoneId: 'visa_application',
    idealTithis: 'Dwitiya, Tritiya, Panchami, Dashami, Trayodashi',
    idealVara: 'Thursday, Friday, Wednesday, Monday',
    idealDirection: 'North-West (Vayavya) — ruled by Moon/Vayu for smooth overseas travel',
    idealDirectionHi: 'वायव्य कोण (उत्तर-पश्चिम) — निर्बाध विदेश यात्रा एवं वीज़ा सिद्धि हेतु',
    purposeRationale: 'Departing under Chara (Movable) nakshatras in favorable Tara Bala facilitates purposeful international transit, smooth visa clearance, and safe passage.',
    purposeRationaleHi: 'चर नक्षत्र में यात्रा प्रारंभ करने से विदेश प्रवास सफल, सुरक्षित एवं उद्देश्यपूर्ण सिद्ध होता है।'
  },
  litigation_competition: {
    labelEn: 'Legal Filing, Arbitration & Contest Resolution',
    labelHi: 'न्यायालयीन वाद दायर, मध्यस्थता एवं प्रतिस्पर्धा विजय',
    milestoneId: 'filing_lawsuit',
    idealTithis: 'Tritiya, Panchami, Saptami, Dashami, Trayodashi',
    idealVara: 'Tuesday (Kuja), Saturday (Shani), Sunday',
    idealDirection: 'South or South-East — for decisive authority and conflict resolution',
    idealDirectionHi: 'दक्षिण अथवा आग्नेय दिशा — कानूनी विजय एवं शत्रु शमन हेतु',
    purposeRationale: 'Filing legal documents under Ugra or Tikshna nakshatras with 6th house support sharpens tactical merit, authoritative evidence presentation, and favorable adjudication.',
    purposeRationaleHi: 'उग्र अथवा तीक्ष्ण नक्षत्र में वाद प्रस्तुत करने से जातक का पक्ष प्रबल एवं न्यायोचित समाधान प्राप्त होता है।'
  },
  spirituality: {
    labelEn: 'Mantra Diksha, Temple Consecration & Anushthana',
    labelHi: 'मंत्र दीक्षा, मंदिर प्रतिष्ठा, अनुष्ठान एवं साधना आरंभ',
    milestoneId: 'anushthana',
    idealTithis: 'Panchami, Ashtami, Ekadashi, Purnima, Amavasya (for specific sadhanas)',
    idealVara: 'Thursday (Guru), Monday (Shiva), Tuesday (Hanuman/Durga)',
    idealDirection: 'North-East (Ishanya) — the abode of Ishwara for spiritual communion',
    idealDirectionHi: 'ईशान कोण (उत्तर-पूर्व) — ध्यान, साधना एवं आत्मसाक्षात्कार हेतु',
    purposeRationale: 'Initiating sadhana in auspicious Tara and benefic Hora awakens divine grace so that the sadhaka shall attain mental absorption and siddhi.',
    purposeRationaleHi: 'शुभ होरा में साधना प्रारंभ करने से चित्त शुद्धि एवं मंत्र सिद्धि शीघ्र प्राप्त होती है।'
  },
  family_parents: {
    labelEn: 'Pitru Tarpan, Family Conclaves & Elder Blessings',
    labelHi: 'पितृ तर्पण, पारिवारिक मिलन एवं वरिष्ठ जनों का आशीर्वाद',
    milestoneId: 'anushthana',
    idealTithis: 'Amavasya, Ashtami, Trayodashi, Purnima',
    idealVara: 'Sunday (Sun/Pitru), Thursday (Guru), Monday (Chandra)',
    idealDirection: 'South (for Pitrus) or East (for Solar blessings)',
    idealDirectionHi: 'दक्षिण (पितृ कार्य) अथवा पूर्व दिशा (सूर्य कृपा हेतु)',
    purposeRationale: 'Offering familial homage in sacred Tithis balances ancestral karmas so that the lineage shall flourish in harmony and peace.',
    purposeRationaleHi: 'पवित्र तिथियों में पारिवारिक एवं पितृ कार्य करने से कुल वृद्धि एवं पितृ दोषों का निवारण होता है।'
  },
  children: {
    labelEn: 'Namakarana (Naming), Annaprashana & Mundan Sanskara',
    labelHi: 'नामकरण, अन्नप्राशन, कर्णवेध एवं मुंडन संस्कार',
    milestoneId: 'namakarana',
    idealTithis: 'Dwitiya, Tritiya, Panchami, Saptami, Dashami, Ekadashi, Trayodashi',
    idealVara: 'Monday, Wednesday, Thursday, Friday',
    idealDirection: 'East or North-East — for vitality, intellect, and longevity',
    idealDirectionHi: 'पूर्व अथवा ईशान कोण — दीर्घायु एवं बौद्धिक तेज हेतु',
    purposeRationale: 'Performing childhood rites under Mridu or Laghu nakshatras bestows health and protection so that the child shall grow with vigor and wisdom.',
    purposeRationaleHi: 'मृदु एवं लघु नक्षत्रों में बालक के संस्कार करने से आरोग्य, दीर्घायु एवं कुशाग्र बुद्धि का वरदान प्राप्त होता है।'
  }
};

export const LifeDomainMuhurtaAid: React.FC<LifeDomainMuhurtaAidProps> = ({
  kundali,
  themeId,
  lang,
  onOpenDetailedMuhurta,
  className = ''
}) => {
  const isHi = lang === 'hi';
  const preset = DOMAIN_MUHURTA_PRESETS[themeId] || DOMAIN_MUHURTA_PRESETS.career_job;
  
  const moonPlanet = kundali.planets.find(p => p.name === 'Moon');
  const janmaNakIdx = moonPlanet?.nakshatraIndex ?? 0;
  const janmaNakName = moonPlanet?.nakshatra ?? 'Ashwini';
  const moonRashi = moonPlanet?.sign ?? 'Aries';

  const [copied, setCopied] = useState(false);

  const handleCopyClientSummary = () => {
    const text = `✨ Vedang Jyotisham • Auspicious Timing Aid for ${kundali.birthDetails.name}\n` +
      `📌 Life Domain: ${isHi ? preset.labelHi : preset.labelEn}\n` +
      `🧭 Ideal Direction (Dik-Shuddhi): ${isHi ? preset.idealDirectionHi : preset.idealDirection}\n` +
      `📅 Auspicious Tithis: ${preset.idealTithis}\n` +
      `🗓️ Favorable Weekdays: ${preset.idealVara}\n` +
      `🌟 Natal Alignment: Janma Nakshatra ${janmaNakName} & ${moonRashi} Chandra Rashi\n` +
      `📜 Classical Blessing: "${isHi ? preset.purposeRationaleHi : preset.purposeRationale}"\n` +
      `*Astrological guidance only. Align with family traditions and operational milestones.*`;
    
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div
      id="life-domain-muhurta-aid"
      className={`bg-gradient-to-br from-amber-50/70 via-white to-orange-50/40 border border-amber-200/90 rounded-2xl p-5 sm:p-6 shadow-2xs space-y-4 ${className}`}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-amber-200/60 pb-3.5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-astro-primary text-white flex items-center justify-center font-bold text-sm shadow-2xs">
            🧭
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm sm:text-base font-bold text-astro-primary font-serif">
                {isHi ? 'जीवन क्षेत्र मुहूर्त एवं कार्यान्वयन परामर्श (Muhurta Aid)' : 'Life Domain Auspicious Timing & Muhurta Aid'}
              </h3>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 px-2 py-0.5 rounded border border-amber-200">
                {isHi ? 'आचार्य व प्रो टूल' : 'Pro & Acharya Aid'}
              </span>
            </div>
            <p className="text-xs text-astro-secondary-text mt-0.5">
              {isHi ? preset.labelHi : preset.labelEn}
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-2 self-start sm:self-auto flex-wrap">
          <button
            onClick={handleCopyClientSummary}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white border border-amber-300 text-astro-primary hover:bg-amber-50 text-xs font-bold transition-all shadow-2xs cursor-pointer min-h-[36px]"
            title="Copy client-ready WhatsApp / SMS summary"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 text-astro-accent" />}
            <span>{copied ? (isHi ? 'कॉपी हो गया' : 'Copied!') : (isHi ? 'पंडित सारांश कॉपी करें' : 'Copy Client Summary')}</span>
          </button>

          {onOpenDetailedMuhurta && (
            <button
              onClick={() => onOpenDetailedMuhurta(preset.milestoneId)}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-astro-primary text-white hover:bg-astro-primary/90 text-xs font-bold transition-all shadow-2xs cursor-pointer min-h-[36px]"
            >
              <Calendar className="w-3.5 h-3.5 text-astro-accent" />
              <span>{isHi ? 'सटीक मुहूर्त खोजें →' : 'Launch Muhurta Advisor →'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Grid of Key Astrological Parameters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Dik-Shuddhi (Direction) */}
        <div className="bg-white/90 border border-amber-200/80 rounded-xl p-3.5 space-y-1.5 shadow-2xs">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text">
            <Compass className="w-3.5 h-3.5 text-astro-accent" />
            <span>{isHi ? 'दिक्-शुद्धि (सम्मुख दिशा)' : 'Auspicious Facing Direction'}</span>
          </div>
          <p className="text-xs font-bold text-astro-primary leading-snug">
            {isHi ? preset.idealDirectionHi : preset.idealDirection}
          </p>
        </div>

        {/* Tithis */}
        <div className="bg-white/90 border border-amber-200/80 rounded-xl p-3.5 space-y-1.5 shadow-2xs">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text">
            <Sparkles className="w-3.5 h-3.5 text-amber-600" />
            <span>{isHi ? 'अनुकूल तिथियाँ' : 'Favorable Tithis'}</span>
          </div>
          <p className="text-xs font-bold text-astro-text leading-snug">
            {preset.idealTithis}
          </p>
        </div>

        {/* Weekdays */}
        <div className="bg-white/90 border border-amber-200/80 rounded-xl p-3.5 space-y-1.5 shadow-2xs">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text">
            <Clock className="w-3.5 h-3.5 text-emerald-600" />
            <span>{isHi ? 'शुभ वार एवं होरा' : 'Recommended Weekdays'}</span>
          </div>
          <p className="text-xs font-bold text-astro-text leading-snug">
            {preset.idealVara}
          </p>
        </div>
      </div>

      {/* Purpose Rationale (Why this time suits) */}
      <div className="bg-white border border-amber-200/90 rounded-xl p-4 space-y-2 shadow-2xs">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span className="text-xs font-bold text-astro-primary font-serif">
              {isHi ? 'शास्त्रीय फलादेश एवं संकल्प' : 'Astrological Rationale & Execution Blessing'}
            </span>
          </div>
          <span className="text-[10px] text-astro-secondary-text font-mono">
            {kundali.birthDetails.name} • {janmaNakName} Nakshatra
          </span>
        </div>
        <p className="text-xs text-astro-text leading-relaxed font-medium">
          {isHi ? preset.purposeRationaleHi : preset.purposeRationale}
        </p>
      </div>

      {/* Disclaimer / Advice Note */}
      <div className="bg-amber-100/50 border border-amber-200 rounded-xl p-2.5 text-[11px] text-amber-950 flex items-start gap-2">
        <AlertTriangle className="w-3.5 h-3.5 text-amber-700 shrink-0 mt-0.5" />
        <p className="leading-tight">
          <strong>{isHi ? 'ज्योतिषीय परामर्श मात्र:' : 'Astrological Advisory Only:'}</strong> {isHi
            ? 'यह समय-विंडो शास्त्रीय वैदिक सिद्धांतों के अनुसार जातक के अनुकूल बनाई गई है। यदि जातक पारिवारिक परंपरा अथवा तत्काल आवश्यकता अनुसार कार्य करना चाहे, तो वह अपनी सुविधानुसार आगे बढ़ सकता है।'
            : 'This timing aid is calculated according to classical Vedic principles. It serves strictly as an advisory — if the native is following guidance from their family priest or has fixed operational deadlines, they may proceed accordingly.'}
        </p>
      </div>
    </div>
  );
};
